import { useState, useRef } from 'react';
import api from '../lib/api';

const ACCEPTED_EXT = '.mp4,.mov,.webm,.avi,.mpeg,.mpg';

function fmtBytes(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3)   return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

export default function VideoUploader({ clientId, liveProductionId, aiProductionId, onUploadComplete }) {
  const [dragging,  setDragging]  = useState(false);
  const [file,      setFile]      = useState(null);
  const [progress,  setProgress]  = useState(0);
  const [phase,     setPhase]     = useState('idle'); // idle | uploading | confirming | error
  const [errorMsg,  setErrorMsg]  = useState('');
  const inputRef = useRef(null);

  function pickFile(f) {
    if (!f) return;
    const ok = /\.(mp4|mov|webm|avi|mpeg|mpg)$/i.test(f.name) || (f.type || '').startsWith('video/');
    if (!ok) { setErrorMsg('Please select a video file (MP4, MOV, WebM, AVI).'); return; }
    setFile(f);
    setErrorMsg('');
    setPhase('idle');
    setProgress(0);
  }

  async function handleUpload() {
    if (!file) return;
    setPhase('uploading');
    setProgress(0);
    setErrorMsg('');
    try {
      const presignRes = await api.post('/api/video-assets/presign', {
        clientId,
        ...(liveProductionId ? { liveProductionId } : { aiProductionId }),
        fileName:    file.name,
        fileSize:    file.size,
        contentType: file.type || 'video/mp4',
      });
      const { uploadUrl, videoAssetId } = presignRes.data;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = e => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`S3 upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.send(file);
      });

      setPhase('confirming');
      const confirmRes = await api.post('/api/video-assets/confirm', { videoAssetId });
      onUploadComplete(confirmRes.data.videoAsset);
    } catch (err) {
      setPhase('error');
      setErrorMsg(err.response?.data?.error || err.message || 'Upload failed.');
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer.files?.[0]);
  }

  const uploading = phase === 'uploading' || phase === 'confirming';

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--violet)' : 'var(--border-mid)'}`,
          borderRadius: 10,
          padding: '20px 16px',
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          background: dragging ? 'rgba(139,109,255,0.07)' : 'var(--bg-input)',
          transition: 'all 0.15s',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXT}
          style={{ display: 'none' }}
          onChange={e => pickFile(e.target.files?.[0])}
        />
        {!file ? (
          <>
            <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.4 }}>▶</div>
            <div style={{ fontSize: 13, color: 'var(--tx-secondary)', fontWeight: 600 }}>Drop video here or click to browse</div>
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4 }}>MP4, MOV, WebM, AVI</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--tx-primary)', fontWeight: 600, marginBottom: 2 }}>{file.name}</div>
            <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{fmtBytes(file.size)}</div>
            {uploading && (
              <div style={{ marginTop: 10 }}>
                <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-card2)', overflow: 'hidden', marginBottom: 5 }}>
                  <div style={{ height: '100%', width: `${phase === 'confirming' ? 100 : progress}%`, background: 'var(--violet)', transition: 'width 0.2s', borderRadius: 2 }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>
                  {phase === 'confirming' ? 'Saving…' : `Uploading ${progress}%`}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {errorMsg && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--s-incomplete-t)' }}>{errorMsg}</div>
      )}

      {file && !uploading && phase !== 'done' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
          <button
            className="btn-ghost"
            style={{ fontSize: 12 }}
            onClick={e => { e.stopPropagation(); setFile(null); setPhase('idle'); setErrorMsg(''); setProgress(0); }}
          >
            Clear
          </button>
          <button
            className="btn-primary"
            style={{ fontSize: 12 }}
            onClick={e => { e.stopPropagation(); handleUpload(); }}
          >
            Upload Video
          </button>
        </div>
      )}
    </div>
  );
}
